import {displayNumber, shortenAddress} from "../../../../utils";
import Donut from "./Donut";
import React, {useContext, useEffect, useMemo, useState} from "react";
import {erc20ABI, useAccount, useBalance, useContractRead, useNetwork} from "wagmi";
import {DEFAULT_CHAIN, SWAP_CONTRACTS} from "../../../../chain/constants";
import {ThemeContext} from "../../../../context/ThemeContext";
import {formatUnits} from "viem";
import CopyToClipboard from "../../CopyToClipboard";
import {useQuery} from "@tanstack/react-query";
import axios from "axios";

export default function UpgradeContract() {
    const { background } = useContext(ThemeContext);

    const { isConnected } = useAccount();
    const { chain, chains } = useNetwork();

    const [currentChain, setCurrentChain] = useState(DEFAULT_CHAIN);
    const [swapContract, setSwapContract] = useState(SWAP_CONTRACTS[DEFAULT_CHAIN.id]);
    useEffect(() => {
        let _currentChain = DEFAULT_CHAIN;
        if (isConnected && chain?.id && chains.findIndex(c => c.id === chain.id) > -1) {
            _currentChain = chain;
        }
        setCurrentChain(_currentChain);
        if (_currentChain.id in SWAP_CONTRACTS) {
            setSwapContract(SWAP_CONTRACTS[_currentChain.id]);
        } else {
            setSwapContract(undefined);
        }
    }, [isConnected, chain, chains]);

    const { data: balanceData } = useBalance({
        address: swapContract?.walletAddress,
        chainId: swapContract?.v2Token?.chainId,
        formatUnits: swapContract?.v2Token?.decimals,
        token: swapContract?.v2Token?.address,
        watch: true,
        enabled: !!swapContract?.v2Token && swapContract?.walletAddress,
    });
    // useEffect(() => console.log(balanceData), [balanceData]);

    const { data: allowanceData } = useContractRead({
        address: swapContract?.v2Token?.address,
        abi: erc20ABI,
        functionName: 'allowance',
        args: [swapContract?.walletAddress, swapContract?.upgradeAddress],
        chainId: swapContract?.v2Token?.chainId,
        watch: true,
        enabled: !!swapContract?.v2Token && swapContract?.walletAddress && swapContract?.upgradeAddress,
    });
    // useEffect(() => console.log(allowanceData), [allowanceData]);

    const [balance, allowance, allowancePercentage, allowancePercentageStr] = useMemo(() => {
        const _balance = balanceData?.formatted ? Number(balanceData.formatted) : 0;
        const _allowance = allowanceData ? Number(formatUnits(allowanceData, swapContract?.v2Token?.decimals || 0)) : 0;
        // console.log({ _balance, _allowance });
        const _allowancePercentage = _balance > 0 && _allowance > 0? _allowance / _balance * 100 : 0;
        return [
            _balance,
            _allowance,
            _allowancePercentage,
            _allowancePercentage > 100 ? '>100' : _allowancePercentage >= 0.1 ? _allowancePercentage.toFixed(_allowancePercentage >= 10? 0: 1) : _allowancePercentage > 0 ? '<0.1' : '0',
        ];
    }, [balanceData, allowanceData, swapContract?.v2Token?.decimals]);

    const { data: tokenHolders } = useQuery({
        queryKey: ['token', swapContract?.v2Token?.chainId, swapContract?.v2Token?.address?.toLowerCase()],
        queryFn: async () => {
            const res = await axios.post(swapContract.subgraphUrl, {
                query: `query ($where: Token_filter) {
  tokens(
    skip: 0
    first: 1
    orderBy: id
    orderDirection: asc
    where: $where
    subgraphError: deny
  ) {
    totalUsers
    timestamp
  }
}`,
                variables: {
                    where: {
                        id: swapContract.v2Token.address.toLowerCase(),
                    },
                },
            });
            return res?.data?.data?.tokens?.[0];
        },
        enabled: swapContract?.v2Token?.chainId && !!swapContract?.subgraphUrl,
        staleTime: 600_000,
        // retry: false,
        refetchOnWindowFocus: true,
        // refetchInterval,
    });

    return (
        <div className="card">
            <div className="card-body">
                <div className="row align-items-start">
                    <div className="col-xl-9 col-lg-12 col-xxl-12">
                        <div className="row">
                            <div className="col-sm-4">
                                <div className="mb-4">
                                    <p className="mb-2">Upgrade Address (V1->V2)</p>
                                    <h4 className="text-black">
                                        {swapContract?.upgradeAddress ?
                                            <>
                                                <a
                                                    href={`${currentChain?.blockExplorers?.default.url}/address/${swapContract.upgradeAddress}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="btn-link"
                                                >
                                                    {shortenAddress(swapContract.upgradeAddress)}
                                                </a>
                                                <CopyToClipboard
                                                    text={swapContract.upgradeAddress}
                                                    btnStyle={{ background: 'transparent', border: 'none', marginLeft: '0.25em' }}
                                                    btnClassName="btn-link"
                                                />
                                            </> : '-'
                                        }
                                    </h4>
                                </div>
                                <div className="mb-4">
                                    <p className="mb-2">Wallet Address</p>
                                    <h4 className="text-black">
                                        {swapContract?.walletAddress ?
                                            <>
                                                <a
                                                    href={`${currentChain?.blockExplorers?.default.url}/address/${swapContract.walletAddress}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="btn-link"
                                                >
                                                    {shortenAddress(swapContract.walletAddress)}
                                                </a>
                                                <CopyToClipboard
                                                    text={swapContract.walletAddress}
                                                    btnStyle={{ background: 'transparent', border: 'none', marginLeft: '0.25em' }}
                                                    btnClassName="btn-link"
                                                />
                                            </> : '-'
                                        }
                                    </h4>
                                </div>
                                <div>
                                    <p className="mb-2">Network</p>
                                    <h4 className="text-black">{currentChain?.name}</h4>
                                </div>
                            </div>
                            <div className="col-sm-4">
                                <div className="mb-4">
                                    <p className="mb-2">V1 Token Address</p>
                                    <h4 className="text-black">
                                        {swapContract?.v1Token?.address ?
                                            <>
                                                <a
                                                    href={`${currentChain?.blockExplorers?.default.url}/address/${swapContract.v1Token.address}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="btn-link"
                                                >
                                                    {shortenAddress(swapContract.v1Token.address)}
                                                </a>
                                                <CopyToClipboard
                                                    text={swapContract.v1Token.address}
                                                    btnStyle={{ background: 'transparent', border: 'none', marginLeft: '0.25em' }}
                                                    btnClassName="btn-link"
                                                />
                                            </> : '-'
                                        }
                                    </h4>
                                </div>
                                <div className="mb-4">
                                    <p className="mb-2">V1 Token Symbol</p>
                                    <h4 className="text-black">{swapContract?.v1Token?.symbol || '-'}</h4>
                                </div>
                                <div className="mb-4">
                                    <p className="mb-2">V1 Token Decimals</p>
                                    <h4 className="text-black">{swapContract?.v1Token?.decimals || '-'}</h4>
                                </div>
                                <div className="mb-4">
                                    <p className="mb-2">V1 Token Holders</p>
                                    <h4 className="text-black">-</h4>
                                </div>
                            </div>
                            <div className="col-sm-4">
                                <div className="mb-4">
                                    <p className="mb-2">V2 Token Address</p>
                                    <h4 className="text-black">
                                        {swapContract?.v2Token?.address ?
                                            <>
                                                <a
                                                    href={`${currentChain?.blockExplorers?.default.url}/address/${swapContract.v2Token.address}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="btn-link"
                                                >
                                                    {shortenAddress(swapContract.v2Token.address)}
                                                </a>
                                                <CopyToClipboard
                                                    text={swapContract.v2Token.address}
                                                    btnStyle={{ background: 'transparent', border: 'none', marginLeft: '0.25em' }}
                                                    btnClassName="btn-link"
                                                />
                                            </> : '-'
                                        }
                                    </h4>
                                </div>
                                <div className="mb-4">
                                    <p className="mb-2">V2 Token Symbol</p>
                                    <h4 className="text-black">{swapContract?.v2Token?.symbol || '-'}</h4>
                                </div>
                                <div className="mb-4">
                                    <p className="mb-2">V2 Token Decimals</p>
                                    <h4 className="text-black">{swapContract?.v2Token?.decimals || '-'}</h4>
                                </div>
                                <div className="mb-4">
                                    <p className="mb-2">V2 Token Holders</p>
                                    <h4 className="text-black">{tokenHolders?.totalUsers || '-'}</h4>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-3 col-lg-12 col-xxl-12 mb-lg-0 mb-3">
                        <p>Allowance/Balance Percent</p>
                        <div className="row align-items-center">
                            <div className="col-sm-6 mb-sm-0 mb-6 text-center">
                                <div className="d-inline-block position-relative donut-chart-sale mb-3">
                                    {background.value === "dark" ? (
                                        <Donut
                                            value={allowancePercentage > 100 ? 100 : allowancePercentage}
                                            backgroundColor="rgb(255, 104, 38)"
                                            backgroundColor2="#2c254a"
                                        />
                                    ) : (
                                        <Donut value={allowancePercentage > 100 ? 100 : allowancePercentage} backgroundColor="rgb(255, 104, 38)"
                                               backgroundColor2="#eee"
                                        />
                                    )}
                                    <small>{allowancePercentageStr}%</small>
                                </div>
                            </div>
                            <div className="col-sm-6 mb-sm-0 mb-6 text-center">
                                <div className="mb-4">
                                    <h4 className="text-black">Allowance</h4>
                                    <p className="mb-2">
                                        {allowance ? displayNumber(allowance) : ''} {swapContract?.v2Token?.symbol || ''}
                                    </p>
                                </div>
                                <div className="mb-4">
                                    <h4 className="text-black">Balance</h4>
                                    <p className="mb-2">
                                        {displayNumber(balance)} {swapContract?.v2Token?.symbol || ''}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
